import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Grid, Tab, Tabs, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import _ from 'lodash';

import getStateManager from '../StateManager';
import { alarmName, alarmValueFormat } from '../Utils/alarm_utils';
import { get_settings_borders_and_units } from '../Constants/constants';
import { LoadSettingsDialog } from '../Utils/load_settings';
import { SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import SettingsSwitch from '../FormWidgets/SettingsSwitch';
import { ShowSetting } from '../FormWidgets/ShowSliderSetting';
import { SettingActions } from '../components';
import { settingControls, useSavedSettings } from './settingFunctions';
import AlarmHelp from '../FormWidgets/AlarmHelp';

export const NO_SUB_ZONE_ID = 0xff;

const CATEGORY_LABELS = {
  climate: 'climate',
  irrigation: 'irrigation',
  nutrition: 'nutrition',
  sensor_error: 'sensor_errors',
  power_extender: 'alarm_category_power_extender',
  gcl: 'alarm_category_gc_link',
  hvac: 'alarm_category_hvac',
  system: 'system',
  other: 'alarm_category_other'
};

function settingAlarmName(entry, id, language, t) {
  return entry ? alarmName(entry, language, 'short') : `${t('alarm_unknown_type')} ${id}`;
}

export const displayAlarmValue = (value, scale = 0) => Number(value) * 10 ** Number(scale);
export const rawAlarmValue = (value, scale = 0) => Math.round(Number(value) / 10 ** Number(scale));

export function alarmCategory(catalogEntry) {
  const category = catalogEntry?.key?.split('.')[0] ?? 'other';
  return category === 'nutrients' ? 'nutrition' : category;
}

export function alarmGroupKey(type, catalogEntry) {
  return `${type}:${alarmCategory(catalogEntry)}`;
}

export function partitionAlarmGroupBySubZone(alarms = []) {
  const subZonesByAlarm = new Map();

  alarms.forEach((entry) => {
    if (entry.catalogEntry?.key?.startsWith('hvac.') || entry.catalogEntry?.instance_kind === 'tank') return;
    const alarmKey = `${entry.type}:${entry.alarm?.alarm_type_id}`;
    const subZone = Number(entry.alarm?.sub_zone_or_id);
    if (!Number.isInteger(subZone) || subZone === NO_SUB_ZONE_ID) return;
    if (!subZonesByAlarm.has(alarmKey)) subZonesByAlarm.set(alarmKey, new Set());
    subZonesByAlarm.get(alarmKey).add(subZone);
  });

  const subZonedAlarmKeys = new Set(
    [...subZonesByAlarm.entries()]
      .filter(([, subZones]) => subZones.size > 1)
      .map(([alarmKey]) => alarmKey)
  );
  const shared = [];
  const subZones = {};

  alarms.forEach((entry) => {
    const alarmKey = `${entry.type}:${entry.alarm?.alarm_type_id}`;
    const subZone = Number(entry.alarm?.sub_zone_or_id);
    if (!subZonedAlarmKeys.has(alarmKey)) {
      shared.push(entry);
      return;
    }

    if (!subZones[subZone]) subZones[subZone] = [];
    subZones[subZone].push(entry);
  });

  return { shared, subZones };
}

function OffsetControl({
  alarm,
  catalogEntry,
  index,
  original,
  controls,
  controller,
  disabled,
  language
}) {
  const { t } = useTranslation();
  const controllerSettings = useSnapshot(controller.settings);
  const {
    unit,
    decimals: numDigits,
    unitDivisor
  } = alarmValueFormat(catalogEntry?.key, catalogEntry?.value ?? { scale: 0, unit: '' }, {
    settings: controllerSettings
  });
  const borders = get_settings_borders_and_units(catalogEntry?.key ?? '') ?? [0, 100, unit];
  const min = borders[0] ?? 0;
  const max = borders[1] ?? 100;
  const basePath = `offset_alarms[${index}]`;
  const originalAlarm = original?.offset_alarms?.[index] ?? alarm;
  const name = settingAlarmName(catalogEntry, alarm.alarm_type_id, language, t);
  const absoluteLow = catalogEntry?.value?.threshold === 'absolute_low';
  const expectedCount = catalogEntry?.value?.threshold === 'expected_count';
  const vwcCeiling = catalogEntry?.value?.threshold === 'vwc_monitor_ceiling';

  const slider = (field, enabledField, label) => (
    <ShowSetting
      key={field}
      id={`${basePath}.${field}`}
      show={label}
      cur_value={alarm[field]}
      controller_value={originalAlarm[field]}
      controller={controller}
      controls={{
        ...controls,
        // Slider conversion can leave floating-point residue in raw wire units.
        setValue: (path, value, saveTemp) => controls.setValue(path, Math.round(value), saveTemp)
      }}
      min={min}
      max={max}
      unit={unit}
      type={numDigits > 0 ? 'float' : undefined}
      num_digits={numDigits}
      mod_digits={unitDivisor}
      disabled={disabled || !alarm[enabledField]}
      valueBoxStyle={{ whiteSpace: 'normal' }}
    >
      <SettingsSwitch
        color="primary"
        checked={Boolean(alarm[enabledField])}
        controller_value={originalAlarm[enabledField]}
        onChange={(event) => controls.setValue(`${basePath}.${enabledField}`, event.target.checked)}
        disabled={disabled}
        inputProps={{ 'aria-label': `${name} ${t(label)}` }}
      />
    </ShowSetting>
  );

  return (
    <SettingGroupGrid
      controller={controller}
      id={`flex_alarm_${alarm.alarm_type_id}_${index}`}
      label={name}
      helpButton={<AlarmHelp ids={[alarm.alarm_type_id]} label={name} />}
      outside
      fullWidth
    >
      {slider(
        'neg_offset',
        'neg_enabled',
        expectedCount
          ? 'alarm_expected_outputs'
          : absoluteLow
          ? 'alarm_minimum_vwc'
          : vwcCeiling
            ? 'alarm_monitor_below_vwc'
            : 'alarm_negative_offset'
      )}
      {!absoluteLow && !vwcCeiling && !expectedCount && slider('pos_offset', 'pos_enabled', 'alarm_positive_offset')}
    </SettingGroupGrid>
  );
}

function EventControl({
  alarm,
  catalogEntry,
  index,
  original,
  controls,
  controller,
  disabled,
  language,
  hvacName
}) {
  const { t } = useTranslation();
  const baseName = settingAlarmName(catalogEntry, alarm.alarm_type_id, language, t);
  const name = catalogEntry?.key?.startsWith('hvac.')
    ? `${baseName}: ${t('alarm_hvac_id')} ${Number(alarm.sub_zone_or_id) + 1}${hvacName ? ` (${hvacName})` : ''}`
    : catalogEntry?.instance_kind === 'tank'
      ? `${baseName}: ${t('alarm_tank_id')} ${Number(alarm.sub_zone_or_id) + 1}`
      : baseName;
  const path = `event_alarms[${index}].enabled`;
  return (
    <ValueBox controller={controller} id={path} style={{ whiteSpace: 'normal' }}>
      <Box display="flex" alignItems="center" width="100%" minWidth={0}>
        <SettingsSwitch
          color="primary"
          checked={Boolean(alarm.enabled)}
          controller_value={original?.event_alarms?.[index]?.enabled}
          onChange={(event) => controls.setValue(path, event.target.checked)}
          disabled={disabled}
          inputProps={{ 'aria-label': name }}
        />
        <Typography
          component="span"
          style={{ minWidth: 0, overflowWrap: 'anywhere', whiteSpace: 'normal' }}
        >
          {name}
          {catalogEntry?.kind === 'occurrence' && (
            <Typography component="span" variant="caption" display="block">
              {t('alarm_one_time_notification')}
            </Typography>
          )}
        </Typography>
        <AlarmHelp ids={[alarm.alarm_type_id]} label={name} />
      </Box>
    </ValueBox>
  );
}

function AlarmControl({ entry, original, controls, controller, writeAccess, language }) {
  const { type, index, alarm, catalogEntry } = entry;
  return type === 'offset' ? (
    <OffsetControl
      key={`offset-${index}`}
      alarm={alarm}
      catalogEntry={catalogEntry}
      index={index}
      original={original}
      controls={controls}
      controller={controller}
      disabled={!writeAccess}
      language={language}
    />
  ) : (
    <EventControl
      key={`event-${index}`}
      alarm={alarm}
      hvacName={entry.hvacName}
      catalogEntry={catalogEntry}
      index={index}
      original={original}
      controls={controls}
      controller={controller}
      disabled={!writeAccess}
      language={language}
    />
  );
}

function AlarmGroup({ alarms, original, controls, controller, writeAccess, language }) {
  const { t } = useTranslation();
  const { shared, subZones } = useMemo(() => partitionAlarmGroupBySubZone(alarms), [alarms]);
  const subZoneIds = useMemo(
    () => Object.keys(subZones).sort((a, b) => Number(a) - Number(b)),
    [subZones]
  );
  const [activeSubZone, setActiveSubZone] = useState(subZoneIds[0] ?? '');

  useEffect(() => {
    if (!subZoneIds.includes(activeSubZone)) setActiveSubZone(subZoneIds[0] ?? '');
  }, [activeSubZone, subZoneIds]);

  const renderAlarm = (entry) => (
    <AlarmControl
      key={`${entry.type}-${entry.index}`}
      entry={entry}
      original={original}
      controls={controls}
      controller={controller}
      writeAccess={writeAccess}
      language={language}
    />
  );

  const renderGrid = (entries) => (
    <Grid container spacing={1}>
      {entries.map((entry) => (
        <Grid key={`${entry.type}-${entry.index}`} item xs={12} md={6}>
          {renderAlarm(entry)}
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box width="100%">
      {shared.length > 0 && renderGrid(shared)}
      {subZoneIds.length > 0 && (
        <Box width="100%" mt={shared.length > 0 ? 1 : 0}>
          <Typography variant="subtitle2" gutterBottom>
            {t('sub_zone')}
          </Typography>
          <Tabs
            value={activeSubZone}
            onChange={(_, value) => setActiveSubZone(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label={t('alarm_sub_zones')}
            style={{ minHeight: 36 }}
          >
            {subZoneIds.map((subZone) => (
              <Tab
                key={subZone}
                value={subZone}
                label={Number(subZone) + 1}
                style={{ minWidth: 48, minHeight: 36, padding: '6px 12px' }}
              />
            ))}
          </Tabs>
          {renderGrid(subZones[activeSubZone] ?? [])}
        </Box>
      )}
    </Box>
  );
}

export default function FlexAlarmSettings() {
  const { id, zone_id } = useParams();
  const { i18n, t } = useTranslation();
  const stateManager = getStateManager();
  const { controller, parent } = stateManager.getControllerForTab({ id, zone_id });
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const catalogState = useSnapshot(stateManager.alarm_catalog_state);
  const settings = useSnapshot(parent.settings);
  const controllerSettings = useSnapshot(controller.settings);
  const original = settings.alarms;
  const [values, setValues] = useState(() => _.cloneDeep(parent.settings.alarms));
  const controls = settingControls(controller, parent, 'alarms', values, setValues);
  const savedSettings = useSavedSettings(parent, controls, 'alarms');
  const catalog = catalogState.entries;
  const writeAccess = controller.access_rights?.alarms?.write === true;

  useEffect(() => {
    setValues(_.cloneDeep(original));
  }, [original]);

  const alarmGroups = useMemo(() => {
    const groups = {};
    const add = (type, index, alarm) => {
      const catalogEntry = catalog?.[Number(alarm.alarm_type_id)];
      const category = alarmCategory(catalogEntry);
      const hvac =
        category === 'hvac' ? controllerSettings.hvac?.[Number(alarm.sub_zone_or_id)] : undefined;
      if (hvac && String(hvac.zone) !== String(parent.zone_id ?? zone_id)) return;
      const groupKey = alarmGroupKey(type, catalogEntry);
      if (!groups[groupKey]) groups[groupKey] = { type, category, alarms: [] };
      groups[groupKey].alarms.push({ type, index, alarm, catalogEntry, hvacName: hvac?.name });
    };
    (values?.offset_alarms ?? []).forEach((alarm, index) => add('offset', index, alarm));
    (values?.event_alarms ?? []).forEach((alarm, index) => add('event', index, alarm));
    return groups;
  }, [
    catalog,
    values?.event_alarms,
    values?.offset_alarms,
    controllerSettings.hvac,
    parent.zone_id,
    zone_id
  ]);

  if (catalogState.error) return <Alert severity="error">{t('alarm_catalog_load_error')}</Alert>;
  if (!catalog || !values) return <CircularProgress aria-label={t('alarm_catalog_loading')} />;

  return (
    <>
      <LoadSettingsDialog
        open={savedSettings.openLoadSettingsDialog}
        handleClose={savedSettings.closeLoadSettingsDialog}
        controls={controls}
      />
      {writeAccess && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={controls.saveSettings}
        />
      )}

      <Box mb={1}>
        <Typography component="span">{t('alarms')} </Typography>
        <AlarmHelp ids={['general']} label={t('alarms')} />
      </Box>

      {Object.entries(alarmGroups).map(([groupKey, group]) => {
        const { alarms, category } = group;
        const hasSubZones = Object.keys(partitionAlarmGroupBySubZone(alarms).subZones).length > 0;
        return (
          <SettingGroupGrid
            key={groupKey}
            controller={controller}
            id={`flex_alarm_category_${groupKey.replace(':', '_')}`}
            fullWidth={hasSubZones}
            label={t(CATEGORY_LABELS[category] ?? CATEGORY_LABELS.other)}
            helpButton={<AlarmHelp ids={[
              ...(group.type === 'offset' && ['climate', 'nutrition'].includes(category) ? ['offsets'] : []),
              ...(group.type === 'offset' && category === 'nutrition' ? ['nutrition'] : []),
              ...(category === 'sensor_error' ? ['sensors'] : []),
              ...(alarms.some(({ alarm }) => [2000, 2001, 2002, 2003].includes(alarm.alarm_type_id)) ? ['tanks'] : [])
            ]} label={t(CATEGORY_LABELS[category] ?? CATEGORY_LABELS.other)} />}
          >
            {hasSubZones ? (
              <AlarmGroup
                alarms={alarms}
                original={original}
                controls={controls}
                controller={controller}
                writeAccess={writeAccess}
                language={i18n.language}
              />
            ) : (
              alarms.map((entry) => (
                <AlarmControl
                  key={`${entry.type}-${entry.index}`}
                  entry={entry}
                  original={original}
                  controls={controls}
                  controller={controller}
                  writeAccess={writeAccess}
                  language={i18n.language}
                />
              ))
            )}
          </SettingGroupGrid>
        );
      })}

      {writeAccess && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={controls.saveSettings}
        />
      )}
    </>
  );
}
